import { Users, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface DivergenceArea {
  topic: string;
  user_a?: string;
  user_b?: string;
  description: string;
}

interface TeamPerspectivesData {
  consensus_areas?: string[];
  divergence_areas?: DivergenceArea[];
  blocking_issues?: string[];
  synthesis?: string;
}

interface TeamPerspectivesSectionProps {
  data?: TeamPerspectivesData | null;
}

export function TeamPerspectivesSection({ data }: TeamPerspectivesSectionProps) {
  if (!data) return null;

  const { consensus_areas, divergence_areas, blocking_issues, synthesis } = data;

  // Don't render if no meaningful content
  if (!consensus_areas?.length && !divergence_areas?.length && !synthesis) return null;

  return (
    <section id="team-perspectives" className="scroll-mt-24 print-section">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Users className="h-5 w-5 text-primary" />
            Team-Perspektiven
          </CardTitle>
          {synthesis && (
            <p className="text-sm text-muted-foreground mt-1">{synthesis}</p>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Consensus */}
          {consensus_areas && consensus_areas.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                Übereinstimmung
              </h4>
              <ul className="space-y-1.5">
                {consensus_areas.map((area, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-green-500 shrink-0" />
                    {area}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Divergence */}
          {divergence_areas && divergence_areas.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                Unterschiedliche Sichtweisen
              </h4>
              <div className="space-y-3">
                {divergence_areas.map((div, i) => (
                  <div key={i} className="rounded-lg border p-3 space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{div.topic}</span>
                      {div.user_a && (
                        <Badge variant="outline" className="text-xs">{div.user_a}</Badge>
                      )}
                      {div.user_b && (
                        <>
                          <span className="text-xs text-muted-foreground">vs.</span>
                          <Badge variant="outline" className="text-xs">{div.user_b}</Badge>
                        </>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{div.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Blocking Issues */}
          {blocking_issues && blocking_issues.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold flex items-center gap-2">
                <XCircle className="h-4 w-4 text-destructive" />
                Blockierende Themen
              </h4>
              <ul className="space-y-1.5">
                {blocking_issues.map((issue, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-destructive shrink-0" />
                    {issue}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
